import React, { useEffect, useRef, useState } from 'react';
import { Container, Card, Button, Row, Col, ListGroup, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import { DesignDocumentationSection, ThreatModelSection } from '../shared/DocumentationIntroContent';
import FloatingViewerButtons from '../shared/FloatingViewerButtons';
import '../shared/AccentButton.css';

function DesignDocumentReview() {
  const { token, refreshAccessToken, getValidToken } = useToken();
  const navigate = useNavigate();

  // keep redirect consistent with the new key
  useProgressRedirect('DOCUMENTATION_INTRO');

  const [selectedDFD, setSelectedDFD] = useState(null);
  const [threatModelProvided, setThreatModelProvided] = useState(false);

  const dfdElements = [
    { id: 'user', label: 'User', threats: ['Spoofing (S)', 'Tampering (T)'] },
    { id: 'web', label: 'Web Server', threats: ['Tampering (T)', 'Information Disclosure (I)'] },
    { id: 'db', label: 'Database', threats: ['Tampering (T)', 'Denial of Service (D)'] },
  ];

  const checkThreatModel = async () => {
    try {
      const authToken = await getValidToken();
      const resp = await fetch('/api' + '/accounts/threat-model', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await resp.json();
      return data.threat_model_provided ?? false;
    } catch (err) {
      console.error('Failed to check threat model', err);
    }
  };

  const handleContinue = async () => {
    try {
      const authToken = await getValidToken();

      const progressRes = await fetch('/api' + '/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: 'PLATFORM_TUTORIAL' }),
      });

      if (progressRes.ok) {
        navigate('/platform-tutorial', { replace: true });
      }
    } catch (err) {
      console.error('Failed to update progress', err);
    }
  };
  // No timer required for this intro step

  useEffect(() => {
    (async () => {
      const hasThreatModel = await checkThreatModel();
      setThreatModelProvided(hasThreatModel);
    })();
  }, []);


  return (
    <Container className="py-5">
      <ProgressTracker currentStep="documentation_intro" />

      <Card className="shadow-lg border-0 mb-4">
        <Card.Body>
          <h2 className="mb-3">📚 Documentation Concepts Intro</h2>
          <p>
            This step gives you time to review the concepts of documentation and design materials for your assigned target before beginning the pentest.
            Take a moment to read the provided documents and diagrams.
          </p>
          <p>
            Please read the provided documents and diagrams. When you're ready, click <strong>Proceed</strong> to continue.
          </p>
          <Alert variant="warning" className="mb-3">
            <strong>⚠️ Note:</strong> The diagrams below are <strong>illustrative examples</strong> only, meant to help you get familiar with each documentation type —
            they are <strong>not</strong> the real target website you will be pentesting.
          </Alert>

          <DesignDocumentationSection />

          {/* Practice: Design Document Viewer */}
          <Row className="mb-4">
            <Col md={12}>
              <div style={{
                borderRadius: 12,
                borderTop: '4px solid #6366f1',
                background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)',
                boxShadow: '0 4px 16px rgba(99,102,241,0.12)',
                padding: '20px 24px',
              }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span style={{
                    background: '#6366f1', color: '#fff',
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
                    padding: '2px 10px', borderRadius: 20, textTransform: 'uppercase',
                  }}>Hands-on Practice</span>
                </div>
                <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                  <div className="flex-grow-1">
                    <h5 className="mb-1" style={{ color: '#3730a3', fontWeight: 700 }}>📄 Try the Design Document Viewer</h5>
                    <p className="mb-0" style={{ color: '#4338ca', fontSize: '0.92rem' }}>
                      Open an interactive sample to get familiar with how the design documents are presented.
                      During the Documentation Review step you will use a similar viewer to answer questions about your assigned website.
                    </p>
                  </div>
                  <Button
                    variant={null}
                    className="tp-accent-btn tp-accent-btn--indigo"
                    style={{
                      whiteSpace: 'nowrap', background: '#6366f1', border: 'none', color: '#fff',
                      fontWeight: 600, padding: '10px 22px', borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
                    }}
                    onClick={() => window.open('/public/design-docs/sample', '_blank')}
                  >
                    Open Sample Viewer
                    <span style={{ display: 'block', fontSize: '0.72em', opacity: 0.85 }}>↗ opens in a new tab</span>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {threatModelProvided && (<>
            <ThreatModelSection />
          {/* Practice: Threat Model Viewer */}
          <Row className="mb-4">
            <Col md={12}>
              <div style={{
                borderRadius: 12,
                borderTop: '4px solid #059669',
                background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                boxShadow: '0 4px 16px rgba(5,150,105,0.12)',
                padding: '20px 24px',
              }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span style={{
                    background: '#059669', color: '#fff',
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
                    padding: '2px 10px', borderRadius: 20, textTransform: 'uppercase',
                  }}>Hands-on Practice</span>
                </div>
                <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                  <div className="flex-grow-1">
                    <h5 className="mb-1" style={{ color: '#065f46', fontWeight: 700 }}>🔐 Try the Threat Model Viewer</h5>
                    <p className="mb-0" style={{ color: '#047857', fontSize: '0.92rem' }}>
                      Open an interactive sample to explore how the DFD and threat list are presented.
                      During the Documentation Review step you will use a similar viewer to answer questions about your assigned website's threat model.
                    </p>
                  </div>
                  <Button
                    variant={null}
                    className="tp-accent-btn tp-accent-btn--green"
                    style={{
                      whiteSpace: 'nowrap', background: '#059669', border: 'none', color: '#fff',
                      fontWeight: 600, padding: '10px 22px', borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(5,150,105,0.35)',
                    }}
                    onClick={() => window.open('/public/threat-model/sample', '_blank')}
                  >
                    Open Sample Viewer
                    <span style={{ display: 'block', fontSize: '0.72em', opacity: 0.85 }}>↗ opens in a new tab</span>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          </>)}

          <div className="d-grid mt-4">
            <Button variant="success" size="lg" onClick={handleContinue}>Proceed to Platform Tutorial</Button>
          </div>
        </Card.Body>
      </Card>

      <FloatingViewerButtons
        onOpenDesignDoc={() => window.open('/public/design-docs/sample', '_blank')}
        onOpenThreatModel={threatModelProvided ? () => window.open('/public/threat-model/sample', '_blank') : null}
      />
    </Container>
  );
}

export default DesignDocumentReview;
